import React, { useState, useEffect, useMemo } from 'react';
import { useOrion } from '../context/OrionContext';
import {
  carregarClientes, filtrarClientes, resolverCampos, criarEmMassa, MAPA_CATEGORIAS,
} from '../services/massaApi';
import { criticidadesDaOcorrencia, ordenarPorNome } from '../services/listasApi';
import { TIPOS_ATENDIMENTO } from '../services/formalizacaoService';
import { avisarComercialNoTeams, notificarEmAtendimento } from '../services/helpdeskApi';
import { ticketsEmAtendimentoDoOperador } from '../services/mysuiteApi';
import { PanelHead, EmptyState } from './ui';
import Icon from './Icon';

// ─────────────────────────────────────────────────────────────────────────────
// Criação de tickets em massa.
//
// Um mesmo problema atingindo vários clientes: escreve-se uma vez e o Orion
// abre um ticket para cada. Três etapas, na ordem em que a decisão acontece:
//
//   1. o que vai no ticket  → título, interação e classificação do lote;
//   2. para quem            → seleção de clientes (com busca);
//   3. revisão e criação    → confere, ajusta a interação de quem precisa,
//                             e acompanha a criação um a um.
//
// A criação é sequencial: são tickets de verdade no MySuite, e disparar tudo
// junto tira a ordem dos números e dificulta saber onde parou se algo falhar.
// ─────────────────────────────────────────────────────────────────────────────

export default function TicketsEmMassa({ contexto = {}, listas, aoFechar }) {
  const { operador, avisar } = useOrion();

  const [etapa, setEtapa] = useState(1);

  // ── Etapa 1: o conteúdo do lote ───────────────────────────────────────────
  const [titulo, setTitulo] = useState(contexto.titulo || '');
  const [interacao, setInteracao] = useState(contexto.interacao || '');
  const [tipo, setTipo] = useState(contexto.tipo || 'Atualização');
  const [ocorrencia, setOcorrencia] = useState(contexto.codigoOcorrencia ? String(contexto.codigoOcorrencia) : '');
  const [criticidade, setCriticidade] = useState(contexto.codigoCriticidade ? String(contexto.codigoCriticidade) : '');
  const [categoria, setCategoria] = useState(contexto.codigoCategoria ? String(contexto.codigoCategoria) : '');
  const [classificacao, setClassificacao] = useState(contexto.classificacao || '');

  // ── Etapa 2: os clientes ──────────────────────────────────────────────────
  const [clientes, setClientes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [busca, setBusca] = useState('');
  const [selecionados, setSelecionados] = useState(() => new Set());

  // ── Etapa 3: criação ──────────────────────────────────────────────────────
  const [interacoesPorCliente, setInteracoesPorCliente] = useState({});
  const [criando, setCriando] = useState(false);
  const [progresso, setProgresso] = useState(null);
  const [resultados, setResultados] = useState(null);

  const opcoesCriticidade = criticidadesDaOcorrencia(listas, ocorrencia);

  useEffect(() => {
    if (etapa !== 2 || clientes.length) return;
    setCarregando(true);
    carregarClientes(contexto.ticketInfo)
      .then(setClientes)
      .catch(err => avisar(`Erro ao carregar clientes: ${err.message}`, { tipo: 'erro' }))
      .finally(() => setCarregando(false));
  }, [etapa, clientes.length, contexto.ticketInfo, avisar]);

  const visiveis = useMemo(() => filtrarClientes(clientes, busca), [clientes, busca]);
  const escolhidos = useMemo(
    () => clientes.filter(c => selecionados.has(c.codigo)),
    [clientes, selecionados]
  );

  const alternar = (codigo) => {
    setSelecionados(atual => {
      const novo = new Set(atual);
      if (novo.has(codigo)) novo.delete(codigo); else novo.add(codigo);
      return novo;
    });
  };

  const criar = async () => {
    if (criando) return;
    setCriando(true);
    setResultados(null);

    try {
      const campos = resolverCampos({ ocorrencia, criticidade, categoria, classificacao }, listas);
      const saida = await criarEmMassa({
        clientes: escolhidos,
        titulo: titulo.trim(),
        interacaoBase: interacao.trim(),
        interacoesPorCliente,
        tipoAtendimento: tipo,
        campos,
        operador,
        codigoOperadorPadrao: operador?.codigo || 0,
        prioridadeContexto: contexto.prioridade,
      }, setProgresso);

      setResultados(saida);
      setProgresso(null);

      const criados = saida.filter(r => r.ok);
      const falhas = saida.length - criados.length;

      // O Comercial é avisado com o lote inteiro de uma vez: um card por ticket
      // encheria o canal quando são dezenas.
      if (tipo === 'Comercial' && criados.length) {
        avisarComercialNoTeams(criados.map(r => r.numero).filter(Boolean));
      }

      // Criou pelo menos um: o n8n recebe a fila do operador. Sem ticket
      // atuado — o lote não tem "o ticket da vez" —, igual ao 1.4.1.
      if (criados.length) {
        ticketsEmAtendimentoDoOperador(operador?.codigo)
          .then(lista => notificarEmAtendimento({
            ticketAtuado: null,
            tickets: lista,
            operadorNome: operador?.nome || '',
            operadorEmail: operador?.email || '',
            codigoOperador: operador?.codigo ?? null,
            solicitante: '',
          }))
          .catch(e => console.warn('[Orion] Em atendimento:', e.message));
      }

      avisar(
        `${criados.length} ticket(s) criado(s)${falhas ? `, ${falhas} com erro` : ''}.`,
        { tipo: falhas ? 'erro' : 'ok', duracao: 9000 }
      );
    } catch (err) {
      avisar(`Erro na criação em massa: ${err.message}`, { tipo: 'erro' });
    } finally {
      setCriando(false);
    }
  };

  const podeAvancar = etapa === 1
    ? (titulo.trim() && interacao.trim())
    : escolhidos.length > 0;

  return (
    <div className="drawer-overlay" onClick={() => !criando && aoFechar()}>
      <div className="modal-nx modal-largo" onClick={e => e.stopPropagation()}>
        <div className="drawer-head">
          <div className="drawer-title">
            Criar tickets em massa
            <span className="drawer-sub">
              {etapa === 1 ? 'o que vai no ticket' : etapa === 2 ? 'para quais clientes' : 'revisão e criação'}
            </span>
          </div>
          <button className="drawer-close" onClick={aoFechar} disabled={criando}>
            <Icon name="x" size={15} />
          </button>
        </div>

        <div className="drawer-body">
          {/* ── Etapa 1 ── */}
          {etapa === 1 && (
            <>
              <label className="field-label">Título do ticket</label>
              <input type="text" value={titulo} onChange={e => setTitulo(e.target.value)} />

              <label className="field-label" style={{ marginTop: 16 }}>Interação (vale para todos)</label>
              <textarea rows={6} value={interacao} onChange={e => setInteracao(e.target.value)} />
              <div className="campo-dica" style={{ marginTop: 6 }}>
                Dá para ajustar cliente a cliente na etapa de revisão.
              </div>

              <div className="detail-grid" style={{ marginTop: 18 }}>
                <div>
                  <label className="field-label">Tipo de atendimento</label>
                  <select value={tipo} onChange={e => setTipo(e.target.value)}>
                    {TIPOS_ATENDIMENTO.map(t => <option key={t.valor} value={t.valor}>{t.rotulo}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label">Categoria (produto)</label>
                  <select value={categoria} onChange={e => setCategoria(e.target.value)}>
                    <option value="">— selecione —</option>
                    {Object.entries(MAPA_CATEGORIAS)
                      .sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'))
                      .map(([nome, cod]) => <option key={cod} value={cod}>{nome}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label">Ocorrência</label>
                  <select value={ocorrencia} onChange={e => setOcorrencia(e.target.value)}>
                    <option value="">— selecione —</option>
                    {ordenarPorNome(listas.ocorrencias).map(o => (
                      <option key={o.codigo} value={o.codigo}>{o.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="field-label">Criticidade</label>
                  <select
                    value={criticidade}
                    onChange={e => setCriticidade(e.target.value)}
                    disabled={!opcoesCriticidade.lista.length}
                  >
                    <option value="">{opcoesCriticidade.mensagem || '— selecione —'}</option>
                    {opcoesCriticidade.lista.map(c => (
                      <option key={c.codigo} value={c.codigo}>{c.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              <label className="field-label" style={{ marginTop: 16 }}>Classificação</label>
              <input
                type="text"
                list="orion-classificacoes-massa"
                value={classificacao}
                onChange={e => setClassificacao(e.target.value)}
              />
              <datalist id="orion-classificacoes-massa">
                {ordenarPorNome(listas.classificacoes).map(c => <option key={c.codigo} value={c.nome} />)}
              </datalist>
            </>
          )}

          {/* ── Etapa 2 ── */}
          {etapa === 2 && (
            <>
              <div className="view-toolbar" style={{ marginBottom: 12 }}>
                <div className="side-search" style={{ flex: 1 }}>
                  <span className="ic"><Icon name="search" size={14} /></span>
                  <input
                    type="text"
                    placeholder="Buscar por nome ou CNPJ"
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                  />
                </div>
                <span className="subhead" style={{ margin: 0 }}>
                  <span className="cnt">{selecionados.size}</span> selecionado(s)
                </span>
              </div>

              {carregando && <EmptyState>Carregando clientes…</EmptyState>}
              {!carregando && !visiveis.length && <EmptyState>Nenhum cliente encontrado.</EmptyState>}

              <div className="massa-lista">
                {visiveis.slice(0, 300).map(c => (
                  <label className="massa-item" key={c.codigo}>
                    <input
                      type="checkbox"
                      checked={selecionados.has(c.codigo)}
                      onChange={() => alternar(c.codigo)}
                    />
                    <span className="massa-nome">{c.nome}</span>
                    {c.cnpj && <span className="massa-cnpj">{c.cnpj}</span>}
                    {/* Quem já tem ticket aberto traz os códigos que o ticket
                        novo precisa — por isso aparece primeiro e marcado. */}
                    {c.temPendente && <span className="pill st-em_atendimento-pill">tem pendente</span>}
                  </label>
                ))}
              </div>

              {visiveis.length > 300 && (
                <div className="campo-dica" style={{ marginTop: 10 }}>
                  Mostrando os 300 primeiros de {visiveis.length}. Refine a busca para ver o resto.
                </div>
              )}
            </>
          )}

          {/* ── Etapa 3 ── */}
          {etapa === 3 && (
            <>
              {!resultados && !criando && (
                <>
                  {/* Âmbar: é a última tela antes de abrir vários tickets de
                      uma vez, e não dá para desfazer. */}
                  <div className="panel panel-dest d-atual">
                    <b>{escolhidos.length} ticket(s)</b> serão criados com o título
                    “{titulo.trim()}” e o tipo <b>{tipo}</b>.
                  </div>
                  <div className="sol-list">
                    {escolhidos.map(c => {
                      // Quem ficou com o texto padrão e quem foi ajustado.
                      // Numa lista de 30 clientes, sem este selo não dá para
                      // saber onde se mexeu sem abrir um por um.
                      const proprio = (interacoesPorCliente[c.codigo] || '').trim();
                      const editado = !!proprio && proprio !== interacao.trim();
                      return (
                        <div className="sol-card" key={c.codigo}>
                          <div className="sol-head">
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="sol-meta">
                                <span className={`selo-dest ${editado ? 'd-info' : 'd-atual'}`}>
                                  <Icon name={editado ? 'pen' : 'copy'} size={10} />
                                  {editado ? 'Editado' : 'Padrão'}
                                </span>
                                {proprio && <span className="sol-id">{proprio.length} caracteres</span>}
                              </div>
                              <div className="sol-desc">{c.nome}</div>
                              <div className="sol-id">{c.cnpj || 'sem CNPJ'}</div>
                            </div>
                          </div>
                          <textarea
                            rows={2}
                            placeholder="Interação específica para este cliente (opcional)"
                            value={interacoesPorCliente[c.codigo] || ''}
                            onChange={e => setInteracoesPorCliente(m => ({ ...m, [c.codigo]: e.target.value }))}
                            style={{ marginTop: 10 }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {criando && progresso && (
                <div className="panel" style={{ marginBottom: 0 }}>
                  <PanelHead
                    titulo="Criando tickets"
                    sub={`${progresso.indice + 1} de ${progresso.total}`}
                  />
                  <div className="massa-progresso">
                    <span style={{ width: `${Math.round(((progresso.indice + 1) / progresso.total) * 100)}%` }} />
                  </div>
                  <div className="campo-dica" style={{ marginTop: 10 }}>
                    {progresso.cliente?.nome}
                  </div>
                </div>
              )}

              {resultados && (
                <>
                  <div className="subhead first">
                    Resultado <span className="cnt">{resultados.filter(r => r.ok).length}/{resultados.length}</span>
                  </div>
                  <div className="sol-list">
                    {resultados.map((r, i) => (
                      <div className="sol-card" key={i}>
                        <div className="sol-head">
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="sol-meta">
                              <span className={`pill ${r.ok ? 'st-concluida-pill' : 'st-devolvida-pill'}`}>
                                {r.ok ? (r.numero ? `#${r.numero}` : 'criado') : 'erro'}
                              </span>
                            </div>
                            <div className="sol-desc">{r.cliente.nome}</div>
                            {!r.ok && <div className="sol-id">{r.erro}</div>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="drawer-foot">
          {etapa > 1 && !criando && !resultados && (
            <button className="btn btn-ghost" onClick={() => setEtapa(e => e - 1)}>
              <Icon name="chevron-left" size={14} /> Voltar
            </button>
          )}

          {etapa < 3 && (
            <button className="btn btn-primary" onClick={() => setEtapa(e => e + 1)} disabled={!podeAvancar}>
              Avançar <Icon name="chevron-right" size={14} />
            </button>
          )}

          {etapa === 3 && !resultados && (
            <button className="btn btn-primary" onClick={criar} disabled={criando || !escolhidos.length}>
              {criando
                ? <><Icon name="rotate-cw" size={14} className="orion-spin" /> Criando…</>
                : <><Icon name="file-plus" size={14} /> Criar {escolhidos.length} ticket(s)</>}
            </button>
          )}

          {resultados && (
            <button className="btn btn-primary" onClick={aoFechar}>Fechar</button>
          )}
        </div>
      </div>
    </div>
  );
}
